import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { getBusinessDate } from "../posOrderId";
import { OrderChannel } from "../../components/constants/OrderChannel";
import { mevStorage } from "../../mev/utils/mevStorage";

const FIRESTORE_TIMEOUT_MS = 4000;

const withFirestoreTimeout = (promise, label) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`[channelCounter] ${label} timed out`));
    }, FIRESTORE_TIMEOUT_MS);
  });
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => clearTimeout(timer));
};

export const getChannelKey = (orderChannel) => {
  const channel = (orderChannel || OrderChannel.COUNTER).toUpperCase();
  if (channel === OrderChannel.TAKEAWAY || channel === "TAKEAWAY") return "TAKEAWAY";
  if (channel === OrderChannel.DELIVERY || channel === "DELIVERY") return "DELIVERY";
  if (channel === OrderChannel.DELIVERY_LOCAL || channel === "DELIVERY_LOCAL") return "DELIVERY";
  if (channel === OrderChannel.TABLE || channel === "TABLE") return "TABLE";
  if (channel === OrderChannel.ONLINE || channel === "ONLINE") return "ONLINE";
  return "COUNTER";
};

export const getChannelPrefix = (orderChannel) => {
  const key = getChannelKey(orderChannel);
  if (key === "TAKEAWAY") return "TW";
  if (key === "DELIVERY") return "DL";
  if (key === "TABLE") return "T";
  if (key === "ONLINE") return "ON";
  return "CS";
};

export const formatBusinessDateKey = (stationStartTime = "04:00") => {
  const businessDate = getBusinessDate(new Date(), stationStartTime || "04:00");
  const year = businessDate.getFullYear();
  const month = String(businessDate.getMonth() + 1).padStart(2, "0");
  const day = String(businessDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const readChannelEnvironment = (storeId) => {
  try {
    const scoped = storeId ? localStorage.getItem(`cloudMevEnvirn_${storeId}`) : "";
    const raw = String(scoped || localStorage.getItem("envirn") || "DEV").trim().toUpperCase();
    if (raw === "DEV" || raw === "ESSAI" || raw === "PROD") return raw;
  } catch {
    // The counter still separates by the default environment.
  }
  return "DEV";
};

export const getChannelCounterRef = (storeId, orderChannel, stationStartTime = "04:00") => {
  const businessDate = formatBusinessDateKey(stationStartTime);
  const channel = getChannelKey(orderChannel);
  const envirn = readChannelEnvironment(storeId);
  const letter = readStationBillLetter(storeId);
  const station = letter === "A" ? "" : `_${letter}`;
  return {
    ref: doc(db, "stores", storeId, "channelCounters", `${envirn}_${businessDate}_${channel}${station}`),
    businessDate,
    channel,
    envirn,
  };
};

const BILL_LETTERS = ["A", "B", "C", "D"];

export const readStationBillLetter = (storeId) => {
  try {
    const id = storeId || localStorage.getItem("selectedStoreId") || "";
    const letter = String(localStorage.getItem(`stationBillLetter_${id}`) || "A").toUpperCase();
    return BILL_LETTERS.includes(letter) ? letter : "A";
  } catch {
    return "A";
  }
};

export const writeStationBillLetter = (storeId, letter) => {
  if (!storeId) return;
  const next = String(letter || "A").toUpperCase();
  localStorage.setItem(`stationBillLetter_${storeId}`, BILL_LETTERS.includes(next) ? next : "A");
};

const stationMark = (storeId) => {
  const letter = readStationBillLetter(storeId);
  return letter === "A" ? "" : letter;
};

const padChannelSequence = (sequence) => {
  const seq = Number(sequence);
  const safeSeq = Number.isFinite(seq) && seq > 0 ? seq : 1;
  return String(safeSeq).padStart(3, "0");
};

export const formatChannelOrderNumber = (orderChannel, sequence, isTrainingMode = false, storeId = "") => {
  const prefix = getChannelPrefix(orderChannel);
  if (getChannelKey(orderChannel) === "TABLE") {
    const seq = Number(sequence);
    return Number.isFinite(seq) ? seq : 0;
  }
  const padded = padChannelSequence(sequence);
  const training = isTrainingMode ? "Z" : "";
  return `${prefix}${stationMark(storeId)}${training}${padded}`;
};

/**
 * Increment the last daily channel value in Firestore.
 * First order of the business day is 1 (CS001). Native persistence serves this offline.
 */
export const nextChannelSequence = async (storeId, orderChannel, stationStartTime = "04:00") => {
  if (!storeId || !orderChannel) {
    throw new Error("storeId and orderChannel are required");
  }

  const { ref, businessDate, channel, envirn } = getChannelCounterRef(storeId, orderChannel, stationStartTime);
  const letter = readStationBillLetter(storeId);
  const storageKey = `channelSeq:${storeId}:${envirn}:${businessDate}:${channel}:${letter}`;
  const legacyKey = `channelSeq:${storeId}:${envirn}:${businessDate}:${channel}`;
  const olderKey = `channelSeq:${storeId}:${businessDate}:${channel}`;
  if (localStorage.getItem(storageKey) == null && letter === "A") {
    const legacy = localStorage.getItem(legacyKey);
    const older = localStorage.getItem(olderKey);
    const carried = legacy != null ? legacy : older;
    if (carried != null) localStorage.setItem(storageKey, carried);
  }
  const next = Number(localStorage.getItem(storageKey) || 0) + 1;
  localStorage.setItem(storageKey, String(next));
  void setDoc(
    ref,
    {
      value: next,
      updated: serverTimestamp(),
      storeId,
      envirn,
      businessDate,
      channel,
      billLetter: letter,
    },
    { merge: true }
  ).catch((error) => {
    console.warn("[channelCounter] Channel number was kept on this station:", error);
  });
  return next;
};

const SALES_CHANNELS = ["COUNTER", "TAKEAWAY", "DELIVERY", "TABLE", "ONLINE"];

/**
 * Puts every ESSAI sales counter back to its start. The next sale in each channel is 1.
 * DEV and PROD counters are left as they are.
 */
export const resetEssaiSalesCounters = async (storeId, stationStartTime = "04:00") => {
  if (!storeId) throw new Error("A store is required");
  if (readChannelEnvironment(storeId) !== "ESSAI") {
    throw new Error("Sales counters can be reset in the test environment only");
  }
  const prefix = `channelSeq:${storeId}:`;
  const dates = new Set([formatBusinessDateKey(stationStartTime)]);
  const remove = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key || !key.startsWith(prefix)) continue;
    const rest = key.slice(prefix.length);
    if (rest.startsWith("DEV:") || rest.startsWith("PROD:")) continue;
    remove.push(key);
    const parts = rest.split(":");
    const date = parts[0] === "ESSAI" ? parts[1] : parts[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(date || "")) dates.add(date);
  }
  const writes = [];
  dates.forEach((businessDate) => {
    SALES_CHANNELS.forEach((channel) => {
      const ref = doc(db, "stores", storeId, "channelCounters", `ESSAI_${businessDate}_${channel}`);
      writes.push(withFirestoreTimeout(setDoc(ref, {
        value: 0,
        updated: serverTimestamp(),
        storeId,
        envirn: "ESSAI",
        businessDate,
        channel,
      }, { merge: true }), "sales counter reset"));
    });
  });
  await Promise.all(writes);
  remove.forEach((key) => localStorage.removeItem(key));
  mevStorage.resetInitialSignature(storeId);
};
